import { useEffect, useRef, useState, type ChangeEvent } from 'react';

interface SearchInputProps {
  /** Committed value (post-debounce), owned by the caller — e.g. `useListQuery`'s `search`. */
  value: string;
  /** Fires `debounceMs` after the last keystroke with the latest raw input value. */
  onChange: (value: string) => void;
  placeholder?: string;
  ariaLabel?: string;
  debounceMs?: number;
  className?: string;
}

/**
 * Debounced search box (#13136) — ported from the AccountList search field pattern.
 * Keeps its own local "raw" echo of the input so typing feels instant, and only calls
 * `onChange` after `debounceMs` of no further keystrokes. The clear button fires
 * `onChange('')` immediately (no debounce wait) since clearing is a deliberate action,
 * not a keystroke to coalesce.
 */
export function SearchInput({
  value,
  onChange,
  placeholder = 'Search…',
  ariaLabel = 'Search',
  debounceMs = 300,
  className = '',
}: SearchInputProps) {
  const [inputValue, setInputValue] = useState(value);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Ref-forward the latest onChange so the debounce timer (started in handleInput,
  // captured in a closure) always calls the current callback rather than a stale one
  // from a prior render — avoids needing onChange in a dependency array that would
  // otherwise force us to restart/rebind the timer on every parent re-render.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Keep the local echo in sync when the controlled `value` changes from outside this
  // component (e.g. a "Clear all filters" action resets the committed search to '').
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setInputValue(next);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => onChangeRef.current(next), debounceMs);
  };

  const handleClear = () => {
    setInputValue('');
    if (timerRef.current) clearTimeout(timerRef.current);
    onChangeRef.current('');
  };

  return (
    <div className={`relative ${className}`}>
      <input
        type="search"
        inputMode="search"
        value={inputValue}
        onChange={handleInput}
        placeholder={placeholder}
        aria-label={ariaLabel}
        className="h-9 w-full min-w-[160px] max-w-sm rounded-md border border-gray-300 pl-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
      />
      {inputValue && (
        <button
          type="button"
          onClick={handleClear}
          aria-label="Clear search"
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}
